import { useMemo } from "react";
import { classifyCategoryGlMapping } from "@common/helpers/rules";
import {
    RuleGroupType,
    TransactionRuleGroup,
} from "@common/types/transactionRule";
import { useWorkspaceAccounts } from "@api/account.api";
import { useWorkspaceRuleGroups } from "@api/rules.api";
import { CategoryGlMappingOverrideRow } from "./useCategoryGlMappingGrid";
import { buildAccountResolver } from "./categoryGlMappingGrid.utils";
import {
    ReconstructableRule,
    filterMatchedAccountingRules,
    reconstructOverrideRows,
} from "./categoryGlMappingReconstruction";

interface UseReconstructedOverrideRowsParams {
    categoryId: string;
    isEnabled: boolean;
}

interface UseReconstructedOverrideRowsResult {
    initialRows: CategoryGlMappingOverrideRow[] | undefined;
    isAdvanced: boolean;
    isLoading: boolean;
    isError: boolean;
}

function extractAccountingRules(
    ruleGroups: TransactionRuleGroup[] | undefined,
): ReconstructableRule[] {
    return (ruleGroups ?? [])
        .filter((group) => group.type === RuleGroupType.ACCOUNTING)
        .flatMap((group) => group.rules ?? []);
}

/**
 * Loads the workspace's accounting rules and accounts, then reconstructs the
 * override rows a custom subcategory's existing rules imply. `initialRows` is
 * `undefined` while loading and defined (possibly `[]`) once both queries settle.
 */
export function useReconstructedOverrideRows({
    categoryId,
    isEnabled,
}: UseReconstructedOverrideRowsParams): UseReconstructedOverrideRowsResult {
    const ruleGroupsQuery = useWorkspaceRuleGroups({ enabled: isEnabled });
    const accountsQuery = useWorkspaceAccounts({ enabled: isEnabled });

    const ruleGroups = ruleGroupsQuery.data?.ruleGroups;
    const accounts = accountsQuery.data;

    const accountingRules = useMemo(
        () => extractAccountingRules(ruleGroups),
        [ruleGroups],
    );

    const initialRows = useMemo<
        CategoryGlMappingOverrideRow[] | undefined
    >(() => {
        if (!isEnabled || ruleGroups === undefined || accounts === undefined) {
            return undefined;
        }
        return reconstructOverrideRows({
            rules: accountingRules,
            accounts,
            categoryId,
        });
    }, [isEnabled, ruleGroups, accounts, accountingRules, categoryId]);

    const isAdvanced = useMemo(() => {
        if (!isEnabled || ruleGroups === undefined || accounts === undefined) {
            return false;
        }
        const matchedRules = filterMatchedAccountingRules(
            accountingRules,
            categoryId,
        );
        return (
            classifyCategoryGlMapping({
                rules: matchedRules,
                resolveAccountIdentity: buildAccountResolver(accounts),
            }) === "advanced"
        );
    }, [isEnabled, ruleGroups, accounts, accountingRules, categoryId]);

    return {
        initialRows,
        isAdvanced,
        isLoading:
            isEnabled && (ruleGroupsQuery.isLoading || accountsQuery.isLoading),
        isError:
            isEnabled && (ruleGroupsQuery.isError || accountsQuery.isError),
    };
}
